export class ConversationsView {
	constructor(state, options = {}) {
		this.state = state;
		this.options = options;
		this.element = null;
		this._unsubscribe = null;
		this.avatarColors = [
			'#155EEF',
			'#8b5cf6',
			'#10b981',
			'#f59e0b',
			'#ef4444',
			'#ec4899',
			'#06b6d4',
		];
	}

	render() {
		this.element = document.createElement('div');
		this.element.className = 'messenger-view messenger-conversations-view';

		this._updateContent();
		this._attachEvents();

		this._unsubscribe = this.state.subscribe((type) => {
			if (
				type === 'conversationsUpdate' ||
				type === 'conversationAdded' ||
				type === 'conversationRead' ||
				type === 'conversationUpdated'
			) {
				this._updateContent();
			}
		});

		return this.element;
	}

	_getAvatarColor(name) {
		// Generate consistent color based on name
		const charCode = (name || 'S').charCodeAt(0);
		return this.avatarColors[charCode % this.avatarColors.length];
	}

	_updateContent() {
		const conversations = this.state.conversations;

		let conversationsHtml;
		if (conversations.length === 0) {
			conversationsHtml = `
            <div class="messenger-empty-state">
                <div class="messenger-empty-state-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 256 256">
                        <path d="M216,48H40A16,16,0,0,0,24,64V224a15.85,15.85,0,0,0,9.24,14.5A16.13,16.13,0,0,0,40,240a15.89,15.89,0,0,0,10.25-3.78.69.69,0,0,0,.13-.11L82.5,208H216a16,16,0,0,0,16-16V64A16,16,0,0,0,216,48ZM40,224h0ZM216,192H82.5a16,16,0,0,0-10.3,3.75l-.12.11L40,224V64H216Z"></path>
                    </svg>
                </div>
                <h3>No conversations yet</h3>
                <p>Start a new conversation with our team</p>
            </div>
        `;
		} else {
			conversationsHtml = `
            <div class="messenger-conversations-list">
                ${conversations.map((conv) => this._renderConversationItem(conv)).join('')}
            </div>
        `;
		}

		this.element.innerHTML = `
        <div class="messenger-conversations-header">
            <h2>Messages</h2>
           <button class="sdk-close-btn" aria-label="Close">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width="18" height="18">
    <rect width="256" height="256" fill="none"/>
    <line x1="200" y1="56" x2="56" y2="200" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
    <line x1="200" y1="200" x2="56" y2="56" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
  </svg>
</button>
        </div>

        <div class="messenger-conversations-body">
            ${conversationsHtml}
        </div>

        <div class="messenger-conversations-footer">
            <button class="messenger-new-message-btn">
                <span>Send us a message</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 256 256" style="flex-shrink: 0;">
                    <rect width="256" height="256" fill="none"/>
                    <line x1="144" y1="128" x2="80" y2="128" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                    <path d="M48.49,221.28A8,8,0,0,0,59.93,231l168-96.09a8,8,0,0,0,0-14l-168-95.85a8,8,0,0,0-11.44,9.67L80,128Z" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="16"/>
                </svg>
            </button>
        </div>
    `;

		this._attachEvents();
	}

	_renderConversationItem(conversation) {
		const unreadClass = conversation.unread > 0 ? 'unread' : '';
		const timeAgo = this._formatTimeAgo(conversation.lastMessageTime);
		const avatarsHtml = this._renderConversationAvatars(
			conversation.participants
		);

		return `
			<div class="messenger-conversation-item ${unreadClass}" data-conversation-id="${conversation.id}">
				<div class="messenger-conversation-avatars">
					${avatarsHtml}
				</div>
				<div class="messenger-conversation-content">
					<div class="messenger-conversation-header">
						<span class="messenger-conversation-title">${conversation.title || 'Chat with team'}</span>
						<span class="messenger-conversation-time">${timeAgo}</span>
					</div>
					<div class="messenger-conversation-preview">
						${conversation.unread > 0 ? '<span class="messenger-unread-dot"></span>' : ''}
						<span class="messenger-conversation-message">${this._truncateMessage(conversation.lastMessage)}</span>
					</div>
				</div>
			</div>
		`;
	}

	_renderConversationAvatars(participants) {
		if (!participants || participants.length === 0) {
			const color = this._getAvatarColor('S');
			return `<div class="sdk-avatar sdk-avatar-md" style="background-color: ${color};">S</div>`;
		}

		const p = participants[0];
		if (p.avatarUrl) {
			return `<div class="sdk-avatar sdk-avatar-md"><img src="${p.avatarUrl}" alt="${p.name}" /></div>`;
		}
		const initial = (p.name || 'S').charAt(0).toUpperCase();
		const color = this._getAvatarColor(p.name);
		return `<div class="sdk-avatar sdk-avatar-md" style="background-color: ${color};">${initial}</div>`;
	}

	_renderAvatarStack() {
		const avatars = this.state.teamAvatars;
		if (!avatars || avatars.length === 0) {
			const color1 = this._getAvatarColor('S');
			const color2 = this._getAvatarColor('T');
			return `
				<div class="messenger-avatar-stack">
					<div class="sdk-avatar sdk-avatar-sm" style="background-color: ${color1};">S</div>
					<div class="sdk-avatar sdk-avatar-sm" style="background-color: ${color2};">T</div>
				</div>
			`;
		}

		const avatarItems = avatars
			.slice(0, 2)
			.map((avatar, index) => {
				if (typeof avatar === 'string' && avatar.startsWith('http')) {
					return `<div class="sdk-avatar sdk-avatar-sm"><img src="${avatar}" alt="Team member" /></div>`;
				}
				const initial = avatar.charAt(0).toUpperCase();
				const color = this._getAvatarColor(avatar);
				return `<div class="sdk-avatar sdk-avatar-sm" style="background-color: ${color};">${initial}</div>`;
			})
			.join('');

		return `<div class="messenger-avatar-stack">${avatarItems}</div>`;
	}

	_formatTimeAgo(timestamp) {
		if (!timestamp) return '';
		const date = new Date(timestamp);
		const now = new Date();
		const diffMs = now - date;
		const diffMins = Math.floor(diffMs / 60000);
		const diffHours = Math.floor(diffMs / 3600000);
		const diffDays = Math.floor(diffMs / 86400000);

		if (diffMins < 1) return 'now';
		if (diffMins < 60) return `${diffMins}m`;
		if (diffHours < 24) return `${diffHours}h`;
		if (diffDays < 7) return `${diffDays}d`;
		return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
	}

	_truncateMessage(message) {
		if (!message) return 'No messages yet';
		const maxLength = 50;
		if (message.length <= maxLength) return message;
		return message.substring(0, maxLength) + '...';
	}

	_attachEvents() {
		const closeBtn = this.element.querySelector('.sdk-close-btn');
		if (closeBtn) {
			closeBtn.addEventListener('click', () => {
				this.state.setOpen(false);
			});
		}

		this.element
			.querySelectorAll('.messenger-conversation-item')
			.forEach((item) => {
				item.addEventListener('click', () => {
					const convId = item.dataset.conversationId;
					this.state.setActiveConversation(convId);
					this.state.markAsRead(convId);
					this.state.setView('chat');

					if (this.options.onSelectConversation) {
						this.options.onSelectConversation(convId);
					}
				});
			});

		const newMsgBtn = this.element.querySelector('.messenger-new-message-btn');
		if (newMsgBtn) {
			newMsgBtn.addEventListener('click', () => {
				this._startNewConversation();
			});
		}
	}

	_startNewConversation() {
		// If there's an open conversation, route to it instead of creating new
		const openConversation = this.state.conversations.find(
			(c) => c.status === 'open'
		);

		if (openConversation) {
			this.state.setActiveConversation(openConversation.id);
			this.state.markAsRead(openConversation.id);
			this.state.setView('chat');
			if (this.options.onSelectConversation) {
				this.options.onSelectConversation(openConversation.id);
			}
		} else {
			this.state.setActiveConversation(null);
			if (this.options.onStartNewConversation) {
				this.options.onStartNewConversation();
			} else {
				this.state.setView('chat');
			}
		}
	}

	destroy() {
		if (this._unsubscribe) {
			this._unsubscribe();
		}
		if (this.element && this.element.parentNode) {
			this.element.parentNode.removeChild(this.element);
		}
	}
}
